import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../components/Breadcrumbs';
import './About.css';

function About() {
    return (
        <>
            <Helmet>
                <title>About NeuAIs - AI-Powered Cloud GPU Platform</title>
                <meta name="description" content="NeuAIs provides accessible, high-performance GPU computing resources for AI researchers and developers. Enterprise-grade infrastructure powered by NVIDIA A100 GPUs." />
                <meta name="keywords" content="GPU computing, AI platform, Jupyter notebooks, cloud GPU, machine learning, deep learning, NVIDIA A100" />
                <link rel="canonical" href="https://lab.neuais.com/about" />
            </Helmet>

            <Breadcrumbs />

            <div className="info-page">
                <div className="info-container">
                    <h1>About NeuAIs</h1>
                    <div className="info-content">
                        <section>
                            <h2>Our Mission</h2>
                            <p>
                                NeuAIs democratizes access to high-performance GPU computing resources for AI researchers,
                                developers, and teams worldwide. Our intelligent time-sharing platform ensures efficient resource
                                allocation while maintaining the flexibility and power of collaborative Jupyter environments.
                            </p>
                            <p>
                                Founded in 2023, we've helped over 5,000 researchers and 200+ companies accelerate their
                                AI/ML workflows, from rapid prototyping to production-scale training.
                            </p>
                        </section>

                        <section>
                            <h2>What We Offer</h2>
                            <ul>
                                <li><strong>NVIDIA A100 GPUs</strong> - On-demand access to 40GB and 80GB variants</li>
                                <li><strong>Jupyter Environments</strong> - Pre-configured with PyTorch, TensorFlow, JAX</li>
                                <li><strong>Real-time Collaboration</strong> - Share sessions and code with team members</li>
                                <li><strong>Smart Scheduling</strong> - Intelligent GPU booking and queue management</li>
                                <li><strong>Pre-installed Tools</strong> - CUDA 12.x, cuDNN 8.x, frameworks ready-to-use</li>
                                <li><strong>Persistent Storage</strong> - Store datasets and models with instant access</li>
                                <li><strong>Custom Environments</strong> - Build and save your own Docker images</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Technology Stack</h2>
                            <p>
                                Built on modern cloud-native technologies:
                            </p>
                            <ul>
                                <li><strong>Kubernetes</strong> - Orchestration for scalability and reliability</li>
                                <li><strong>Docker</strong> - Containerized environments for complete isolation</li>
                                <li><strong>WebSockets</strong> - Real-time terminal and collaboration features</li>
                                <li><strong>PostgreSQL</strong> - Reliable data persistence and session management</li>
                                <li><strong>Redis</strong> - Fast caching and queue management</li>
                            </ul>
                            <p>
                                Each session runs in its own isolated container with dedicated GPU allocation,
                                ensuring security and performance consistency.
                            </p>
                        </section>

                        <section>
                            <h2>Use Cases</h2>
                            <ul>
                                <li><strong>Research</strong> - Universities and research labs running experiments</li>
                                <li><strong>Model Training</strong> - LLMs, computer vision, reinforcement learning</li>
                                <li><strong>Education</strong> - Teaching ML/AI courses with hands-on GPU access</li>
                                <li><strong>Prototyping</strong> - Rapid iteration before production deployment</li>
                                <li><strong>Data Science</strong> - Large-scale data processing and analysis</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Learn More</h2>
                            <p>
                                <Link to="/contact">Contact our team</Link> for enterprise pricing and custom solutions, or
                                <Link to="/privacy"> review our privacy practices</Link>.
                            </p>
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
}

export default About;
